import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollTop from "@/components/feature/ScrollTop";
import { channels } from "@/mocks/channels";
import { topics } from "@/mocks/topics";
import { formatNum } from "@/lib/format";
import { useSeo } from "@/hooks/useSeo";

const all = [...topics].sort((a, b) => b.heat - a.heat);

type SortKey = "heat" | "comments" | "updated";

const chipMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-200 text-secondary-800",
};

export default function Rank() {
  const [sort, setSort] = useState<SortKey>("heat");
  useSeo({
    title: "全网热议榜 - 全网吃瓜 · 网友评论与舆论风向排行",
    description:
      "全网吃瓜热议榜：汇集当下最热门的讨论话题，附网友评论热度、舆论支持/反对占比与情绪走向，快速掌握全网都在围观什么。",
    keywords: "全网吃瓜,热议榜,网友评论,舆论风向,热议观点",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "全网热议榜 - 全网吃瓜",
      description: "全网热门讨论话题排行榜，含网友评论与舆论风向。",
    },
  });

  const list = useMemo(() => {
    const arr = [...all];
    if (sort === "heat") arr.sort((a, b) => b.heat - a.heat);
    else if (sort === "comments") arr.sort((a, b) => b.comments - a.comments);
    return arr;
  }, [sort]);

  const totalComments = topics.reduce((s, t) => s + t.comments, 0);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="wrap py-10 md:py-14">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="mb-6 text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
            </li>
            <li className="text-foreground-300">/</li>
            <li className="font-medium text-foreground-800">全网热议榜</li>
          </ol>
        </nav>

        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="flex items-center gap-2 text-sm font-bold text-primary-600">
              <i className="ri-fire-fill" aria-hidden="true" /> 实时更新
            </span>
            <h1 className="mt-2 font-display text-3xl font-bold text-foreground-900 md:text-4xl">
              全网热议榜
            </h1>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">
              按讨论热度排序的全站围观风向榜。点击任意话题，直接查看各方态度与舆论速览。
            </p>
          </div>
          <div className="flex flex-col gap-1.5 rounded-2xl border border-background-200 bg-background-100 px-5 py-3 text-sm">
            <span className="font-display text-2xl font-bold text-foreground-900">{formatNum(totalComments)}</span>
            <span className="text-xs text-foreground-500">当前累计收录讨论 · 持续更新中</span>
          </div>
        </div>

        {/* 排序 */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">排序：</span>
          {[
            { key: "heat" as SortKey, label: "讨论热度" },
            { key: "comments" as SortKey, label: "评论数量" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setSort(o.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                sort === o.key
                  ? "bg-primary-500 font-bold text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* 榜单列表 */}
        <div className="mt-6 grid gap-3 lg:grid-cols-2">
          {list.map((t, i) => {
            const ch = channels.find((c) => c.slug === t.channel);
            const rankBg =
              i === 0
                ? "bg-primary-500 text-background-50"
                : i === 1
                  ? "bg-primary-200 text-primary-800"
                  : i === 2
                    ? "bg-secondary-300 text-secondary-900"
                    : "bg-background-200 text-foreground-600";
            return (
              <article
                key={t.slug}
                className="group flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-200 hover:bg-background-100/60"
              >
                <span
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-display text-xl font-bold ${rankBg}`}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${chipMap[ch?.accent ?? "secondary"]}`}>
                      {ch?.label ?? "话题"}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-flashlight-fill text-accent-500" aria-hidden="true" />热度 {formatNum(t.heat)}
                    </span>
                    <span className="text-xs text-foreground-400">{formatNum(t.comments)} 条讨论</span>
                  </div>
                  <h2 className="mt-1.5 font-heading text-base font-semibold leading-snug text-foreground-900">
                    <Link to={`/topics/${t.slug}`} className="transition-colors group-hover:text-primary-700">
                      {t.title}
                    </Link>
                  </h2>
                  <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">{t.dek}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full">
                      <div className="flex h-full w-full">
                        <span className="bg-accent-500" style={{ width: `${t.sentiment.support}%` }} />
                        <span className="bg-secondary-300" style={{ width: `${t.sentiment.neutral}%` }} />
                        <span className="bg-primary-500" style={{ width: `${t.sentiment.oppose}%` }} />
                      </div>
                    </div>
                    <span className="whitespace-nowrap text-xs text-foreground-500">
                      <span className="font-semibold text-accent-700">{t.sentiment.support}%</span> 支持
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>
      <Footer />
      <ScrollTop />
    </div>
  );
}